import { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain, Sparkles, TrendingDown, ShieldCheck, AlertTriangle, Lightbulb,
  RefreshCw, Loader2, IndianRupee, Receipt, ArrowDownRight, Clock,
  ChevronDown, ChevronUp, BadgePercent, Landmark, FileText, Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAccounting } from "@/contexts/AccountingContext";
import { useExpenses } from "@/contexts/ExpenseContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useToast } from "@/hooks/use-toast";
import { computeFinancialMetrics } from "@/lib/accounting/financialEngine";
import { supabase } from "@/integrations/supabase/client";

const fmt = (n: number) => `₹${n.toLocaleString("en-IN")}`;

interface TaxAdvice {
  summary: string;
  taxSavings: { title: string; description: string; section?: string; estimatedSavings?: number; priority: string; actionRequired: string }[];
  expenseOptimizations: { title: string; description: string; currentSpend?: number; potentialSaving?: number; recommendation: string }[];
  gstOpportunities: { title: string; description: string; amount?: number; action: string; deadline?: string }[];
  complianceAlerts: { title: string; description: string; severity: string; deadline?: string; penalty?: string }[];
  effectiveTaxRate?: number;
  totalPotentialSavings: number;
}

function PriorityBadge({ priority }: { priority: string }) {
  const styles: Record<string, string> = {
    high: "bg-destructive/10 text-destructive border-destructive/20",
    medium: "bg-warning/10 text-warning border-warning/20",
    low: "bg-accent/10 text-accent border-accent/20",
  };
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${styles[priority] || styles.low}`}>{priority}</span>;
}

function SeverityBadge({ severity }: { severity: string }) {
  const styles: Record<string, string> = {
    critical: "bg-destructive/10 text-destructive border-destructive/20",
    warning: "bg-warning/10 text-warning border-warning/20",
    info: "bg-accent/10 text-accent border-accent/20",
  };
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${styles[severity] || styles.info}`}>{severity}</span>;
}

function AdvisoryCard({ icon: Icon, title, description, children, accentColor = "accent" }: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  children?: React.ReactNode;
  accentColor?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-border bg-card overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full p-4 flex items-start gap-3 text-left hover:bg-muted/20 transition-colors">
        <div className={`w-8 h-8 rounded-lg bg-${accentColor}/10 flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <Icon className={`h-4 w-4 text-${accentColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-foreground">{title}</h4>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{description}</p>
        </div>
        {children && (expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />)}
      </button>
      <AnimatePresence>
        {expanded && children && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4 pt-0">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const AITaxAdvisorPage = () => {
  const { vouchers, ledgers } = useAccounting();
  const { expenses } = useExpenses();
  const { activeCompany } = useCompany();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [advice, setAdvice] = useState<TaxAdvice | null>(null);
  const [aiPowered, setAiPowered] = useState(false);

  const financialSummary = useMemo(() => {
    const m = computeFinancialMetrics({
      vouchers,
      ledgers,
      expenses,
    });

    const expenseSummary = expenses.reduce((acc, e) => {
      acc[e.category] = (acc[e.category] || 0) + e.amount;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalRevenue: m.netSalesRevenue,
      totalPurchases: m.taxablePurchases,
      totalReceipts: m.totalReceipts,
      totalPayments: m.totalPayments,
      cogs: m.cogs,
      grossProfit: m.grossProfit,
      netProfit: m.netProfit,
      gstOutput: m.gstOutput,
      gstInput: m.gstInput,
      gstNet: m.netGstDifference,
      gstStatusLabel: m.gstStatusLabel,
      totalReceivable: m.totalReceivable,
      totalPayable: m.totalPayable,
      totalIndirectExpenses: m.operatingExpenses,
      salesCount: m.salesVouchersCount,
      purchaseCount: m.purchaseVouchersCount,
      expenseByCategory: expenseSummary,
      pendingVouchers: vouchers.filter((v) => v.status === "Pending").length,
    };
  }, [vouchers, ledgers, expenses]);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/ai/tax-advisor", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyId: activeCompany?.id || null }),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || "AI service is currently unavailable. Please try again later.");
      }

      setAdvice(json.data);
      setAiPowered(true);
      toast({ title: "AI Tax Analysis Complete", description: "Recommendations generated using AccouPro AI (Gemma-4)." });
    } catch (err: any) {
      console.error("AI tax advisor failed:", err);
      toast({
        title: "AI Service Notice",
        description: err.message || "AI Tax Advisor is currently unavailable. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [activeCompany, toast]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Landmark className="h-6 w-6 text-accent" />
            AI Tax Advisor
          </h1>
          <p className="text-sm text-muted-foreground">AI-powered tax optimization, GST advisory & compliance insights</p>
        </div>
        <div className="flex items-center gap-2">
          {aiPowered && (
            <span className="text-[10px] uppercase tracking-wider text-accent font-medium bg-accent/10 px-2.5 py-1 rounded-full flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> AI Powered
            </span>
          )}
          <Button onClick={runAnalysis} disabled={loading} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />}
            {loading ? "Analyzing..." : advice ? "Re-analyze" : "Run AI Analysis"}
          </Button>
        </div>
      </div>

      {/* Financial snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Revenue", value: financialSummary.totalRevenue, icon: TrendingDown, color: "success" },
          { label: "Net Profit", value: financialSummary.netProfit, icon: IndianRupee, color: financialSummary.netProfit >= 0 ? "success" : "destructive" },
          { label: "GST Payable", value: Math.max(0, financialSummary.gstNet), icon: Receipt, color: "warning" },
          { label: "Input Credit", value: financialSummary.gstInput, icon: ArrowDownRight, color: "accent" },
        ].map((item) => (
          <div key={item.label} className="p-3 rounded-lg bg-card border border-border">
            <div className="flex items-center gap-2 mb-1">
              <item.icon className={`h-3.5 w-3.5 text-${item.color}`} />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{item.label}</span>
            </div>
            <p className="text-lg font-bold text-foreground">{fmt(item.value)}</p>
          </div>
        ))}
      </div>

      {/* Analysis results */}
      {advice ? (
        <div className="space-y-4">
          {/* Summary banner */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-xl bg-gradient-to-r from-accent/10 via-accent/5 to-transparent border border-accent/20">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-5 w-5 text-accent" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-semibold text-foreground">AI Tax Advisory Summary</h3>
                  {advice.effectiveTaxRate && (
                    <span className="text-[10px] font-medium bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                      Effective Tax Rate: {advice.effectiveTaxRate.toFixed(1)}%
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{advice.summary}</p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs font-bold text-success bg-success/10 px-2 py-1 rounded-lg">
                    Potential Savings: {fmt(advice.totalPotentialSavings)}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          <Tabs defaultValue="tax-savings">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="tax-savings" className="gap-1.5"><BadgePercent className="h-3.5 w-3.5" /> Tax Savings ({advice.taxSavings.length})</TabsTrigger>
              <TabsTrigger value="expenses" className="gap-1.5"><TrendingDown className="h-3.5 w-3.5" /> Expenses ({advice.expenseOptimizations.length})</TabsTrigger>
              <TabsTrigger value="gst" className="gap-1.5"><Receipt className="h-3.5 w-3.5" /> GST/ITC ({advice.gstOpportunities.length})</TabsTrigger>
              <TabsTrigger value="compliance" className="gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Compliance ({advice.complianceAlerts.length})</TabsTrigger>
            </TabsList>

            {/* Tax Savings */}
            <TabsContent value="tax-savings" className="space-y-3 mt-4">
              {advice.taxSavings.map((item, i) => (
                <AdvisoryCard key={i} icon={Lightbulb} title={item.title} description={item.description}>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <PriorityBadge priority={item.priority} />
                      {item.section && <span className="text-[10px] font-mono bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{item.section}</span>}
                      {item.estimatedSavings && <span className="text-xs font-bold text-success">{fmt(item.estimatedSavings)} potential savings</span>}
                    </div>
                    <div className="p-2.5 rounded-lg bg-success/5 border border-success/15">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Action Required</span>
                      <p className="text-xs text-foreground mt-0.5">{item.actionRequired}</p>
                    </div>
                  </div>
                </AdvisoryCard>
              ))}
            </TabsContent>

            {/* Expense Optimizations */}
            <TabsContent value="expenses" className="space-y-3 mt-4">
              {advice.expenseOptimizations.map((item, i) => (
                <AdvisoryCard key={i} icon={TrendingDown} title={item.title} description={item.description}>
                  <div className="space-y-2">
                    {(item.currentSpend || item.potentialSaving) && (
                      <div className="flex items-center gap-4">
                        {item.currentSpend && <div><span className="text-[10px] text-muted-foreground">Current Spend</span><p className="text-xs font-bold text-foreground">{fmt(item.currentSpend)}</p></div>}
                        {item.potentialSaving && <div><span className="text-[10px] text-muted-foreground">Potential Saving</span><p className="text-xs font-bold text-success">{fmt(item.potentialSaving)}</p></div>}
                      </div>
                    )}
                    <div className="p-2.5 rounded-lg bg-accent/5 border border-accent/15">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Recommendation</span>
                      <p className="text-xs text-foreground mt-0.5">{item.recommendation}</p>
                    </div>
                  </div>
                </AdvisoryCard>
              ))}
            </TabsContent>

            {/* GST/ITC Opportunities */}
            <TabsContent value="gst" className="space-y-3 mt-4">
              {advice.gstOpportunities.map((item, i) => (
                <AdvisoryCard key={i} icon={Receipt} title={item.title} description={item.description} accentColor="accent">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      {item.amount && <span className="text-xs font-bold text-success bg-success/10 px-2 py-0.5 rounded">{fmt(item.amount)} opportunity</span>}
                      {item.deadline && <span className="text-[10px] text-warning flex items-center gap-1"><Clock className="h-3 w-3" /> Deadline: {item.deadline}</span>}
                    </div>
                    <div className="p-2.5 rounded-lg bg-accent/5 border border-accent/15">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Action</span>
                      <p className="text-xs text-foreground mt-0.5">{item.action}</p>
                    </div>
                  </div>
                </AdvisoryCard>
              ))}
            </TabsContent>

            {/* Compliance Alerts */}
            <TabsContent value="compliance" className="space-y-3 mt-4">
              {advice.complianceAlerts.map((item, i) => (
                <AdvisoryCard key={i} icon={AlertTriangle} title={item.title} description={item.description} accentColor={item.severity === "critical" ? "destructive" : "warning"}>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <SeverityBadge severity={item.severity} />
                      {item.deadline && <span className="text-[10px] text-warning flex items-center gap-1"><Clock className="h-3 w-3" /> {item.deadline}</span>}
                    </div>
                    {item.penalty && (
                      <div className="p-2.5 rounded-lg bg-destructive/5 border border-destructive/15">
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Potential Penalty</span>
                        <p className="text-xs text-destructive font-medium mt-0.5">{item.penalty}</p>
                      </div>
                    )}
                  </div>
                </AdvisoryCard>
              ))}
            </TabsContent>
          </Tabs>
        </div>
      ) : !loading ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
            <Brain className="h-8 w-8 text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Ready to analyze your finances</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
            AccouPro AI Tax Advisor will analyze your revenue, expenses, GST data, and ledger balances to identify tax savings, optimize expenses, and ensure compliance.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-lg mx-auto">
            {[
              { icon: BadgePercent, label: "Tax Savings" },
              { icon: TrendingDown, label: "Expense Optimization" },
              { icon: Receipt, label: "GST/ITC Credits" },
              { icon: ShieldCheck, label: "Compliance Alerts" },
            ].map((f) => (
              <div key={f.label} className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-muted/30 border border-border/50">
                <f.icon className="h-4 w-4 text-accent" />
                <span className="text-[10px] font-medium text-muted-foreground">{f.label}</span>
              </div>
            ))}
          </div>
          <Button onClick={runAnalysis} className="mt-6 gap-2">
            <Brain className="h-4 w-4" /> Run AI Tax Analysis
          </Button>
        </div>
      ) : (
        <div className="text-center py-16">
          <Loader2 className="h-10 w-10 text-accent animate-spin mx-auto mb-4" />
          <h3 className="text-sm font-semibold text-foreground">Analyzing your financial data...</h3>
          <p className="text-xs text-muted-foreground mt-1">AccouPro AI is reviewing ledgers, vouchers, GST data & expenses</p>
        </div>
      )}
    </div>
  );
};

// Fallback local analysis when AI is unavailable
function generateLocalAdvice(data: Record<string, any>): TaxAdvice {
  const taxSavings = [];
  const expenseOptimizations = [];
  const gstOpportunities = [];
  const complianceAlerts = [];
  let totalPotentialSavings = 0;

  // Tax savings based on profit
  if (data.netProfit > 0) {
    const sec80c = Math.min(150000, data.netProfit * 0.1);
    taxSavings.push({
      title: "Maximize Section 80C Deductions",
      description: "Invest in eligible instruments like PPF, ELSS, or life insurance to reduce taxable income.",
      section: "Section 80C",
      estimatedSavings: Math.round(sec80c * 0.3),
      priority: "high",
      actionRequired: `Invest up to ${fmt(150000)} in 80C eligible instruments before March 31st.`,
    });
    totalPotentialSavings += Math.round(sec80c * 0.3);
  }

  if (data.netProfit > 500000) {
    taxSavings.push({
      title: "Consider Presumptive Taxation (44AD)",
      description: "If turnover is below ₹2 Cr, consider Section 44AD for simplified taxation at 8% of turnover.",
      section: "Section 44AD",
      estimatedSavings: Math.round(data.netProfit * 0.05),
      priority: "medium",
      actionRequired: "Evaluate if presumptive taxation scheme provides lower tax liability than regular assessment.",
    });
    totalPotentialSavings += Math.round(data.netProfit * 0.05);
  }

  // GST opportunities
  if (data.gstInput > 0) {
    const unclaimed = Math.round(data.gstInput * 0.15);
    gstOpportunities.push({
      title: "Unclaimed Input Tax Credit",
      description: `Based on your purchase patterns, approximately ${fmt(unclaimed)} in ITC may be unclaimed. Cross-verify with GSTR-2B.`,
      amount: unclaimed,
      action: "Run GST Reconciliation to match purchase invoices with GSTR-2B and claim all eligible ITC.",
      deadline: "20th of next month (GSTR-3B deadline)",
    });
    totalPotentialSavings += unclaimed;
  }

  if (data.gstNet > 0) {
    gstOpportunities.push({
      title: "GST Payment Optimization",
      description: `Net GST payable is ${fmt(data.gstNet)}. Ensure all eligible purchase invoices are recorded before filing.`,
      amount: Math.round(data.gstNet * 0.1),
      action: "Record any pending purchase invoices before GSTR-3B filing to maximize ITC offset.",
    });
  }

  // Expense optimizations
  if (data.totalIndirectExpenses > data.totalRevenue * 0.3) {
    expenseOptimizations.push({
      title: "High Indirect Expense Ratio",
      description: `Indirect expenses are ${((data.totalIndirectExpenses / data.totalRevenue) * 100).toFixed(1)}% of revenue — above the recommended 25% threshold.`,
      currentSpend: data.totalIndirectExpenses,
      potentialSaving: Math.round(data.totalIndirectExpenses * 0.1),
      recommendation: "Review top expense categories and negotiate vendor contracts. Consider automating recurring processes to reduce overhead.",
    });
    totalPotentialSavings += Math.round(data.totalIndirectExpenses * 0.1);
  }

  expenseOptimizations.push({
    title: "Digitize Expense Documentation",
    description: "Ensure all expenses have proper documentation for tax deductibility.",
    recommendation: "Upload all receipts and invoices to the Documents module. Undocumented expenses may be disallowed during assessment.",
  });

  // Compliance alerts
  complianceAlerts.push({
    title: "GSTR-1 Filing Due",
    description: "Monthly GSTR-1 must be filed by the 11th of next month with details of all outward supplies.",
    severity: "warning",
    deadline: "11th of next month",
    penalty: "Late fee of ₹50/day (CGST + SGST) up to ₹10,000",
  });

  complianceAlerts.push({
    title: "Advance Tax Installment",
    description: "If estimated tax liability exceeds ₹10,000, advance tax must be paid quarterly.",
    severity: "info",
    deadline: "15th of Mar, Jun, Sep, Dec",
    penalty: "Interest under Section 234C at 1% per month on shortfall",
  });

  if (data.pendingVouchers > 0) {
    complianceAlerts.push({
      title: `${data.pendingVouchers} Pending Vouchers`,
      description: "Unapproved vouchers may affect financial reporting accuracy.",
      severity: "warning",
    });
  }

  return {
    summary: `Your business shows revenue of ${fmt(data.totalRevenue)} with net profit of ${fmt(data.netProfit)}. GST liability stands at ${fmt(Math.max(0, data.gstNet))} with ${fmt(data.gstInput)} in input credits. There are opportunities to optimize tax outflow and maximize deductions.`,
    taxSavings,
    expenseOptimizations,
    gstOpportunities,
    complianceAlerts,
    effectiveTaxRate: data.netProfit > 0 ? 25.17 : 0,
    totalPotentialSavings,
  };
}

export default AITaxAdvisorPage;
